'use client';

import { MessagePrimitive, useAuiState } from '@assistant-ui/react';
import { type JSX, type ReactNode, useMemo } from 'react';

import { formatTimestamp } from '../../_utils';

// ── Shared layout ────────────────────────────────────────────────────

interface UserMessageLayoutProps {
    timestamp?: string;
    children: ReactNode;
}

/**
 * Presentational layout for a user message bubble.
 * Shared between the runtime-connected UserMessage and the static visitor demo.
 */
export function UserMessageLayout({ timestamp, children }: UserMessageLayoutProps): JSX.Element {
    return (
        <div className="flex flex-col items-end">
            <div className="flex items-center gap-4 mb-2 w-full max-w-[85%]">
                <div className="flex items-center gap-1 text-sm text-gray-500 ml-auto">
                    {timestamp && <span>{timestamp}</span>}
                    <svg
                        width="11"
                        height="8"
                        viewBox="0 0 11 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="[&_path]:stroke-gray-400"
                    >
                        <path
                            d="M9.75 0.75L3.75 6.75L0.75 3.75"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>
            <div className="max-w-[85%] rounded-lg bg-white border border-gray-200 px-4 py-2.5">
                <div className="text-sm text-gray-900">{children}</div>
            </div>
        </div>
    );
}

// ── Runtime-connected component ──────────────────────────────────────

/**
 * Returns the formatted timestamp for the current message.
 *
 * Prefers the API-provided ISO string (historical messages) and falls back
 * to the runtime's createdAt (live messages). Both selectors return stable
 * primitives to avoid re-render loops in useAuiState.
 */
function useMessageTimestamp(): string {
    const apiTimestamp = useAuiState(
        ({ message }) => (message.metadata?.custom?.createdAt as string | undefined) ?? null,
    );
    const runtimeTimestampMs = useAuiState(({ message }) => message.createdAt?.getTime() ?? null);

    return useMemo(() => {
        const date = apiTimestamp ? new Date(apiTimestamp) : runtimeTimestampMs ? new Date(runtimeTimestampMs) : null;
        return date ? formatTimestamp(date) : '';
    }, [apiTimestamp, runtimeTimestampMs]);
}

export function UserMessage(): JSX.Element {
    const timestamp = useMessageTimestamp();

    return (
        <MessagePrimitive.Root className="py-3" data-role="user">
            <UserMessageLayout timestamp={timestamp}>
                <MessagePrimitive.Parts />
            </UserMessageLayout>
        </MessagePrimitive.Root>
    );
}
